/**
 * BirdsPass.ts — instanced renderer for the BirdsSpawner output.
 *
 * Geometry: a tiny procedural bird (3 triangles, 9 vertices) in bird-local
 * coords. Forward = +X, wingspan along ±Z. The vertex shader bends the
 * wing tips with a sin flap and yaws each instance to its heading.
 *
 * Depth-tested against the scene depth so birds correctly disappear behind
 * distant hills, but no depth write — they composite cleanly over water.
 */

import wgsl from "../shaders/birds.wgsl?raw";
import type { ManagedRenderTarget } from "../rendering/RenderTarget";
import { REVERSE_Z_COMPARE } from "../rendering/RenderTarget";
import type { FrameContext, RenderPass } from "../rendering/RenderPass";
import type { Camera } from "../Camera";
import type { LightingUniforms } from "../rendering/LightingUniforms";
import {
  BIRDS_CAPACITY,
  BIRD_STRIDE_BYTES,
  BirdsSpawner,
} from "../sim/BirdsSpawner";
import { createBufferFromTypedArray } from "../gpu/Buffers";

/** Procedural bird silhouette in bird-local coords (forward +X, span ±Z). */
function buildBirdMesh(): Float32Array {
  // Each triangle: 3 vec3 vertices.
  // Body: a flat triangle (beak forward, two rear corners on the spine).
  // Wings: triangles from the body's spine out to the wingtip.
  const v: number[] = [
    // Body
    0.6, 0.0, 0.0, -0.6, 0.0, -0.25, -0.6, 0.0, 0.25,
    // Left wing (negative Z)
    0.3, 0.0, -0.25, -0.3, 0.0, -0.25, -0.05, 0.0, -1.4,
    // Right wing (positive Z)
    0.3, 0.0, 0.25, -0.05, 0.0, 1.4, -0.3, 0.0, 0.25,
  ];
  return new Float32Array(v);
}

export class BirdsPass implements RenderPass {
  readonly name = "BirdsPass";

  private device: GPUDevice;
  private pipeline: GPURenderPipeline;
  private bg: GPUBindGroup;
  private vertexBuffer: GPUBuffer;
  private instanceBuffer: GPUBuffer;
  private paramsBuffer: GPUBuffer;
  private paramsCpu = new Float32Array(4);
  private spawner: BirdsSpawner;
  private vertexCount: number;

  constructor(
    device: GPUDevice,
    rt: ManagedRenderTarget,
    camera: Camera,
    lighting: LightingUniforms,
    spawner: BirdsSpawner,
  ) {
    this.device = device;
    this.spawner = spawner;

    const mesh = buildBirdMesh();
    this.vertexCount = mesh.length / 3;
    this.vertexBuffer = createBufferFromTypedArray(
      device,
      mesh,
      GPUBufferUsage.VERTEX | GPUBufferUsage.COPY_DST,
      "BirdsPass.mesh",
    );
    this.instanceBuffer = device.createBuffer({
      label: "BirdsPass.instances",
      size: BIRDS_CAPACITY * BIRD_STRIDE_BYTES,
      usage: GPUBufferUsage.VERTEX | GPUBufferUsage.COPY_DST,
    });
    this.paramsBuffer = device.createBuffer({
      label: "BirdsPass.params",
      size: 16,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });

    const module = device.createShaderModule({
      label: "birds.wgsl",
      code: wgsl,
    });
    const bgl = device.createBindGroupLayout({
      label: "BirdsPass.bgl",
      entries: [
        {
          binding: 0,
          visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT,
          buffer: { type: "uniform" },
        },
        {
          binding: 1,
          visibility: GPUShaderStage.FRAGMENT,
          buffer: { type: "uniform" },
        },
        {
          binding: 2,
          visibility: GPUShaderStage.VERTEX,
          buffer: { type: "uniform" },
        },
      ],
    });
    this.bg = device.createBindGroup({
      label: "BirdsPass.bg",
      layout: bgl,
      entries: [
        { binding: 0, resource: { buffer: camera.buffer } },
        { binding: 1, resource: { buffer: lighting.buffer } },
        { binding: 2, resource: { buffer: this.paramsBuffer } },
      ],
    });

    this.pipeline = device.createRenderPipeline({
      label: "BirdsPass.pipeline",
      layout: device.createPipelineLayout({ bindGroupLayouts: [bgl] }),
      vertex: {
        module,
        entryPoint: "vs_main",
        buffers: [
          {
            arrayStride: 12,
            stepMode: "vertex",
            attributes: [{ shaderLocation: 0, offset: 0, format: "float32x3" }],
          },
          {
            arrayStride: BIRD_STRIDE_BYTES,
            stepMode: "instance",
            attributes: [
              { shaderLocation: 1, offset: 0, format: "float32x4" },
              { shaderLocation: 2, offset: 16, format: "float32x4" },
            ],
          },
        ],
      },
      fragment: {
        module,
        entryPoint: "fs_main",
        targets: [
          {
            format: rt.current.colorFormat,
            blend: {
              color: {
                srcFactor: "one",
                dstFactor: "one-minus-src-alpha",
                operation: "add",
              },
              alpha: {
                srcFactor: "one",
                dstFactor: "one-minus-src-alpha",
                operation: "add",
              },
            },
          },
        ],
      },
      // Birds are thin and viewable from any angle; skip culling.
      primitive: { topology: "triangle-list", cullMode: "none" },
      depthStencil: {
        format: rt.current.depthFormat,
        depthWriteEnabled: false,
        depthCompare: REVERSE_Z_COMPARE,
      },
    });
  }

  encode(encoder: GPUCommandEncoder, frame: FrameContext): void {
    if (!frame.state.showBirds) return;
    const n = this.spawner.instanceCount;
    if (n === 0) return;

    this.device.queue.writeBuffer(
      this.instanceBuffer,
      0,
      this.spawner.cpuBuffer,
      0,
      n * BIRD_STRIDE_BYTES,
    );
    this.paramsCpu[0] = frame.time;
    this.paramsCpu[1] = 11.0; // flap frequency (rad/s)
    this.paramsCpu[2] = 0;
    this.paramsCpu[3] = 0;
    this.device.queue.writeBuffer(this.paramsBuffer, 0, this.paramsCpu);

    const pass = encoder.beginRenderPass({
      label: "BirdsPass",
      colorAttachments: [
        {
          view: frame.renderTarget.colorView,
          loadOp: "load",
          storeOp: "store",
        },
      ],
      depthStencilAttachment: {
        view: frame.renderTarget.depthView,
        depthLoadOp: "load",
        depthStoreOp: "store",
      },
    });
    pass.setPipeline(this.pipeline);
    pass.setBindGroup(0, this.bg);
    pass.setVertexBuffer(0, this.vertexBuffer);
    pass.setVertexBuffer(1, this.instanceBuffer);
    pass.draw(this.vertexCount, n, 0, 0);
    pass.end();
  }

  destroy(): void {
    this.vertexBuffer.destroy();
    this.instanceBuffer.destroy();
    this.paramsBuffer.destroy();
  }
}
